/**
 * Industry Research v2.0 - TypeScript Types
 * Matches backend Pydantic models from src/models/industry_research.py
 */

import type { RegionConfig, UnifiedFinancialRecord } from './financial';
export type { RegionConfig };

// ============================================================================
// ENUMS
// ============================================================================

export enum IndustryStatus {
  ACTIVE = 'active',
  EMERGING = 'emerging',
  DECLINING = 'declining',
  MATURE = 'mature',
  SUNSET = 'sunset',
}

export enum DataRefreshStatus {
  CURRENT = 'current',
  STALE = 'stale',
  PENDING = 'pending',
  FAILED = 'failed',
}

export enum MaturityStage {
  NASCENT = 'nascent',
  GROWTH = 'growth',
  MATURE = 'mature',
  DECLINING = 'declining',
}

// ============================================================================
// INDUSTRY SUMMARY (for list views)
// ============================================================================

export interface IndustrySummary {
  industryId: string;
  name: string;
  displayName: string;
  category: string;
  status: IndustryStatus;
  maturityStage?: MaturityStage;
  currentVersion?: string; // e.g., "v2.1.0"
  currentVersionId?: string;
  totalVersions: number;
  activeCaseCount: number;
  dataRefreshStatus: DataRefreshStatus;
  lastDataRefreshAt?: string; // ISO datetime
  createdAt: string; // ISO datetime
  createdBy: string;
  sicCodes?: string[];
  naicsCodes?: string[];
  gicsCode?: string;
}

// ============================================================================
// INDUSTRY DETAIL (for detail view)
// ============================================================================

export interface IndustryDetail extends IndustrySummary {
  // Current version research data
  marketData?: MarketData;
  competitiveLandscape?: CompetitiveLandscape;
  technologyTrends?: TechnologyTrends;
  disruptions?: Disruptions;
  executiveSummary?: ExecutiveSummary;
}

// ============================================================================
// RESEARCH DATA COMPONENTS
// ============================================================================

export interface MarketData {
  totalMarketSize: string; // e.g., "125.5B"
  marketGrowthRate: number; // percentage
  marketByRegion: Record<string, string>; // { "North America": "45.2B", ... }
  fastestGrowingSegment: string;
  averageGrossMargin: number;
  primaryDrivers: string[];
  currency: string;
}

export interface CompetitiveLandscape {
  competitiveIntensity: string; // "very_high" | "high" | "moderate" | "low"
  marketConcentration: string; // "monopoly" | "oligopoly" | "fragmented"
  herfindahlIndex?: number;
  topCompetitorCount: number;
  marketLeaders: MarketLeader[];
  emergingPlayers: string[];
  barrierToEntry: string;
  threatOfSubstitutes?: string;
  buyerPower?: string;
  supplierPower?: string;
}

export interface MarketLeader {
  companyId?: string | null;
  companyName: string;
  ticker?: string | null;
  cik?: string | null;
  country?: string | null;
  marketShare: number;
  revenue?: string;
  strengths: string[];
  weaknesses: string[];
  existsInCompanyResearch?: boolean;
  secMatchConfidence?: number | null;
  // Client-side flag set after "Add to Company Research" while the
  // background research pipeline is still fetching financial data.
  // The "View in Company Research" link stays disabled until cleared.
  isProcessing?: boolean;
}

export interface TechnologyTrends {
  emergingTechnologies: string[];
  adoptionStage: string; // "nascent" | "growth" | "mature" | "declining"
  technologyReadiness: string;
  digitalMaturity: string;
  innovationDrivers: string[];
  rdSpendTrend: string;
  patentActivity?: string;
  automationLevel?: string;
  keyTechnologies: TechnologyTrend[];
}

export interface TechnologyTrend {
  name: string;
  adoptionRate: string; // e.g., "65%"
  maturityLevel: string;
  impact: string; // "transformational" | "significant" | "moderate"
  timeline: string; // e.g., "1-2 years"
}

export interface Disruptions {
  disruptionRisk: string; // "low" | "moderate" | "high" | "critical"
  disruptiveForces: Disruption[];
  regulatoryChanges: string[];
  sustainabilityPressures: string[];
  geopoliticalRisks: string[];
  supplyChainRisks?: string[];
  climateImpact?: string;
  consumerPreferenceShifts?: string[];
  activeDisruptions: string[];
}

export interface Disruption {
  name: string;
  type: string; // "technology" | "regulatory" | "market" | "social"
  severity: string; // "low" | "moderate" | "high" | "critical"
  timeline: string; // "immediate" | "near-term" | "long-term"
  probability: string; // "very_high" | "high" | "moderate" | "low"
  impact: string;
  mitigation: string;
}

export interface ExecutiveSummary {
  overallSummary: string;
  keyInsights: string[];
  industryHealth: string; // "excellent" | "strong" | "healthy" | "concerning" | "declining"
  industryAttractiveness: string; // "very_attractive" | "attractive" | "neutral" | "unattractive"
  opportunityScore: number; // 0.0-1.0
  riskScore: number; // 0.0-1.0
  growthOutlook: string; // "very_positive" | "positive" | "neutral" | "negative"
  investmentAppeal?: string;
  strategicImplications: string[];
  criticalSuccessFactors: string[];
}

// ============================================================================
// REQUEST/RESPONSE MODELS
// ============================================================================

export interface CheckOrCreateIndustryRequest {
  name: string;
  category?: string;
  sicCodes?: string[];
  naicsCodes?: string[];
  gicsCode?: string;
  source?: string; // "company_search" | "manual"
  geographicScope?: string;
}

export interface CheckOrCreateIndustryResponse {
  industryId: string;
  name: string;
  status: IndustryStatus;
  existed: boolean; // true if already existed, false if newly created
  currentVersion?: string;
  currentVersionId?: string;
  lastResearchedAt?: string;
  createdAt: string;
  sicCodes?: string[];
  naicsCodes?: string[];
  gicsCode?: string;
  dataRefreshStatus: DataRefreshStatus;
}

export interface GenerateIndustryResearchRequest {
  name: string;
  displayName?: string;
  category?: string;
  sicCodes?: string[];
  naicsCodes?: string[];
  gicsCode?: string;
  geographicScope?: string;
  forceRegenerate?: boolean;
}

export interface IndustryFilters {
  search: string;
  status?: IndustryStatus;
  category?: string;
  dataRefreshStatus?: DataRefreshStatus;
  sortBy: 'name' | 'updated' | 'created' | 'cases';
  sortOrder: 'asc' | 'desc';
}

// ============================================================================
// VERSION TYPES
// ============================================================================

export interface IndustryVersion {
  versionId: string;
  semanticVersion: string;
  reportPeriod: string;
  createdAt: string;
  changeType: string;
  changeDescription?: string;
  materialityScore: number;
  materialityAssessment?: string;
}

export interface IndustryTimeline {
  industryId?: string;
  totalVersions: number;
  timeline: IndustryVersion[];
}

// ============================================================================
// UI HELPER TYPES
// ============================================================================

export const STATUS_COLORS: Record<IndustryStatus, string> = {
  [IndustryStatus.ACTIVE]: 'bg-green-100 text-green-800',
  [IndustryStatus.EMERGING]: 'bg-blue-100 text-blue-800',
  [IndustryStatus.MATURE]: 'bg-gray-100 text-gray-800',
  [IndustryStatus.DECLINING]: 'bg-orange-100 text-orange-800',
  [IndustryStatus.SUNSET]: 'bg-red-100 text-red-800',
};

export const DATA_REFRESH_STATUS_COLORS: Record<DataRefreshStatus, string> = {
  [DataRefreshStatus.CURRENT]: 'bg-green-100 text-green-800',
  [DataRefreshStatus.STALE]: 'bg-yellow-100 text-yellow-800',
  [DataRefreshStatus.PENDING]: 'bg-orange-100 text-orange-800',
  [DataRefreshStatus.FAILED]: 'bg-red-100 text-red-800',
};

export const DATA_REFRESH_STATUS_LABELS: Record<DataRefreshStatus, string> = {
  [DataRefreshStatus.CURRENT]: '✓ Current',
  [DataRefreshStatus.STALE]: '⚠️ Stale',
  [DataRefreshStatus.PENDING]: '⏳ Pending Research',
  [DataRefreshStatus.FAILED]: '✗ Failed',
};

// ============================================================================
// INDUSTRY FINANCIAL BENCHMARKS
// ============================================================================

/**
 * Per-year aggregated (median) financial metrics across industry market leaders.
 * Mirrors UnifiedFinancialRecord shape so existing chart components work directly.
 * Decimal fields (roic, wacc, ebitda_margin) match UnifiedFinancialRecord conventions.
 */
export interface IndustryBenchmarkRecord {
  year: number;
  fiscal_year_label?: string | null;

  // Revenue
  total_revenue?: number | null;
  revenue_yoy_growth?: number | null;   // decimal (0.05 = 5%)

  // Profitability
  gross_profit?: number | null;
  ebitda?: number | null;
  ebitda_margin?: number | null;        // decimal (0.25 = 25%)
  ebit?: number | null;
  net_profit?: number | null;

  // Valuation
  market_cap?: number | null;
  enterprise_value?: number | null;
  ev_ebitda?: number | null;

  // EV Breakdown
  ev_from_ebit?: number | null;
  ev_from_future_growth?: number | null;
  ev_from_future_market_expectation?: number | null;
  cap?: number | null;

  // Capital metrics
  invested_capital?: number | null;
  roic?: number | null;                 // decimal (0.15 = 15%)
  roic_percentage?: number | null;      // percentage (15.0)
  wacc?: number | null;                 // decimal
  wacc_percentage?: number | null;      // percentage
  nopat?: number | null;

  // Debt
  total_debt?: number | null;
  net_debt?: number | null;
  shareholders_funds?: number | null;
  debt_equity_ratio?: number | null;

  // Tax
  tax_rate?: number | null;
  tax_rate_percentage?: number | null;

  contributing_companies: number;
}

/** Latest-year financial snapshot for a single market leader (comparison table row) */
export interface CompanyFinancialSummary {
  company_name: string;
  market_share?: number | null;
  ticker?: string | null;

  latest_year?: number | null;
  latest_revenue?: number | null;
  latest_ebitda?: number | null;
  latest_ebitda_margin?: number | null;  // percentage (25.0 = 25%)
  latest_ebit?: number | null;
  latest_net_profit?: number | null;
  latest_roic?: number | null;           // percentage (15.0 = 15%)
  latest_wacc?: number | null;           // percentage
  latest_ev_ebitda?: number | null;
  latest_debt_equity?: number | null;
  latest_ev?: number | null;
  latest_net_debt?: number | null;
  latest_market_cap?: number | null;
  latest_invested_capital?: number | null;

  // vs-median flags
  above_median_revenue?: boolean | null;
  above_median_ebitda_margin?: boolean | null;
  above_median_roic?: boolean | null;
  above_median_ev_ebitda?: boolean | null;

  years_of_data: number;
  has_financial_data: boolean;
}

/** Full financial time series for one market leader — used in multi-company charts */
export interface CompanyTimeSeries {
  company_name: string;
  ticker?: string | null;
  data: UnifiedFinancialRecord[];  // sorted ascending by year
}

/** Full response from GET /api/v2/financial/industry/{industry_id} */
export interface IndustryBenchmarkResponse {
  success: boolean;
  message?: string | null;
  industry_id: string;
  industry_name: string;
  region: RegionConfig;

  companies_included: string[];
  companies_count: number;
  years_available: number;

  aggregated_data: IndustryBenchmarkRecord[];  // sorted ascending by year
  latest_summary?: IndustryBenchmarkRecord | null;
  company_details: CompanyFinancialSummary[];
  companies_time_series: CompanyTimeSeries[];   // full per-company time series for charts
}

/**
 * Calculate data age in days from lastDataRefreshAt timestamp
 */
export function getDataAge(lastDataRefreshAt?: string): number | null {
  if (!lastDataRefreshAt) return null;
  const refreshDate = new Date(lastDataRefreshAt);
  const now = new Date();
  const diffMs = now.getTime() - refreshDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Get freshness indicator based on data age
 * Green < 30 days, Yellow 30-90 days, Red > 90 days
 */
export function getFreshnessColor(dataAge: number | null): string {
  if (dataAge === null) return 'text-gray-500';
  if (dataAge < 30) return 'text-green-600';
  if (dataAge < 90) return 'text-yellow-600';
  return 'text-red-600';
}
